import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Watch, CheckCircle2, Settings, XCircle, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { startSamsungHealthSync, stopSamsungHealthSync } from '@/lib/samsungHealth';

export function SamsungHealthConnect() {
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) return;

    const checkConnection = async () => {
      try {
        const { data, error } = await supabase
          .from('samsung_health_tokens')
          .select('*')
          .eq('user_id', currentUser.id)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          console.error('Error checking Samsung Health connection:', error);
          return;
        }

        setConnected(!!data);

        // Start sync if connected
        if (data) {
          await startSamsungHealthSync(currentUser.id);
        }
      } catch (error) {
        console.error('Error checking Samsung Health connection:', error);
      }
    };

    checkConnection();

    // Cleanup sync on unmount
    return () => {
      stopSamsungHealthSync();
    };
  }, [currentUser]);

  const handleConnect = async () => {
    if (!currentUser) return;

    try {
      setConnecting(true);

      // Simuler une connexion réussie
      const { error } = await supabase
        .from('samsung_health_tokens')
        .upsert({
          user_id: currentUser.id,
          access_token: 'simulated_token',
          refresh_token: 'simulated_refresh_token',
          expiry_date: new Date(Date.now() + 3600000).toISOString() // 1 heure
        });

      if (error) throw error;

      // Démarrer la synchronisation
      await startSamsungHealthSync(currentUser.id);

      setConnected(true);
      toast({
        title: "Montre connectée",
        description: "Votre Samsung Galaxy Fit 3 est maintenant connectée.",
      });
    } catch (error) {
      console.error('Error connecting Samsung Health:', error);
      toast({
        variant: "destructive",
        title: "Erreur de connexion",
        description: "Impossible de connecter votre montre. Veuillez réessayer.",
      });
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!currentUser) return;

    try {
      setConnecting(true);

      // Arrêter la synchronisation
      stopSamsungHealthSync();

      const { error } = await supabase
        .from('samsung_health_tokens')
        .delete()
        .eq('user_id', currentUser.id);

      if (error) throw error;

      setConnected(false);
      toast({
        title: "Montre déconnectée",
        description: "Votre Samsung Galaxy Fit 3 a été déconnectée.",
      });
    } catch (error) {
      console.error('Error disconnecting Samsung Health:', error);
      toast({
        variant: "destructive",
        title: "Erreur de déconnexion",
        description: "Impossible de déconnecter votre montre. Veuillez réessayer.",
      });
    } finally {
      setConnecting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Watch className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold">Samsung Galaxy Fit 3</h2>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {connected ? (
            <>
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle2 className="w-5 h-5" />
                <span>Montre connectée</span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleDisconnect}
                  disabled={connecting}
                  className="flex-1"
                >
                  {connecting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Déconnexion...
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 mr-2" />
                      Déconnecter
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/projector-watch/settings')}
                  className="flex-1"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Paramètres
                </Button>
              </div>
            </>
          ) : (
            <Button
              onClick={handleConnect}
              disabled={connecting}
              className="w-full"
            >
              {connecting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Connexion...
                </>
              ) : (
                <>
                  <Watch className="w-4 h-4 mr-2" />
                  Connecter ma montre
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}